import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import Layout from "../layouts/layout";
import Modal from "../components/modal";
import Spinner from "../components/spinner";
import BILLING from "../api/services/BILLING";
import Loader from "../components/loader";
import { useBusinessCapabilities } from "../hooks/useBusinessCapabilities";
import SelectPlan from "../components/selectPlan";
import Payment from "../components/payment";
import { paths } from "../routes/paths";
import cashRegisterLottie from "../assets/lottie-cash-register.json";
import styles from "../styles/_screens/paymentSettings.module.scss";

// EZcount withdrawn — Morning is the only invoice provider offered.
const EXTERNAL_PROVIDERS = ["greeninvoice"];
const PROVIDER_LABEL = { greeninvoice: "Morning" };

const PaymentSettings = () => {
  const navigate = useNavigate();
  const { isPro, loading: capLoading } = useBusinessCapabilities();
  let businessId = localStorage.getItem("user_id");
  businessId = JSON.parse(businessId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingCreds, setExistingCreds] = useState(null);

  // Invoice-provider form (the ONLY editable fields on this screen now —
  // acquiring is handled entirely by the Hyp connect/disconnect flow below).
  const [formdata, setFormdata] = useState({
    invoiceProvider: "",
    greenInvoiceApiKey: "",
    greenInvoiceSecret: "",
  });

  // Invoice provider account info (fetched when credentials exist)
  const [morningAccount, setMorningAccount] = useState(null);

  // Numbering-acknowledgment gate
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);

  // ── Hyp acquiring (self-serve connect / disconnect) ──
  const [hypLoading, setHypLoading] = useState(true);
  const [hypBusy, setHypBusy] = useState(false);
  const [hypLive, setHypLive] = useState(false);
  const [hypMasof, setHypMasof] = useState("");
  const [hypOnboardingLink, setHypOnboardingLink] = useState("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  // Upgrade-to-PRO, in place. Deliberately the SAME SelectPlan + Payment pair the
  // subscription screen uses: card details go to the one already-audited endpoint
  // instead of a second payment path written for this screen.
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // A business whose stored invoiceProvider is the legacy "zcredit" value: the
  // select shows unselected + a small note. Only true until they pick a real
  // external provider and save.
  const isLegacyZcredit =
    !!existingCreds && existingCreds.invoiceProvider === "zcredit";
  const isExternal = EXTERNAL_PROVIDERS.includes(formdata.invoiceProvider);
  const invoiceAcknowledgedAt = existingCreds?.invoiceAcknowledgedAt || null;

  // ── FETCH EXISTING PAYMENT CREDS ──
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await BILLING.get_payment_cred();
        if (res && res.data) {
          const d = res.data;
          setExistingCreds(d);
          setFormdata({
            // Legacy "zcredit" / unknown values render as unselected — the two
            // the only supported invoice provider is Morning.
            invoiceProvider: EXTERNAL_PROVIDERS.includes(d.invoiceProvider)
              ? d.invoiceProvider
              : "",
            greenInvoiceApiKey: d.greenInvoiceApiKey || "",
            greenInvoiceSecret: d.greenInvoiceSecret || "",
          });
          if (d.greenInvoiceApiKey) fetchMorningAccount();
        }
      } catch (error) {
        // no existing creds is fine
        console.log(error);
      }
      setLoading(false);
    };
    fetchData();
    fetchHypStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMorningAccount = async () => {
    try {
      const res = await BILLING.get_morning_account();
      if (res?.data) setMorningAccount(res.data);
    } catch (_) {}
  };

  // ── Hyp acquiring status ──
  const fetchHypStatus = async () => {
    try {
      const res = await BILLING.get_hyp_status();
      const d = res?.data || {};
      setHypLive(!!d.live);
      setHypMasof(d.hypMasof || "");
      setHypOnboardingLink(d.maxOnboardingLink || "");
    } catch (e) {
      // keep the card visible; a failed status read just shows "not connected"
      console.log("fetchHypStatus:", e?.message);
    } finally {
      setHypLoading(false);
    }
  };

  // ── Connect to Hyp (open their personal Max signup link) ──
  const handleHypConnect = async () => {
    setHypBusy(true);
    try {
      let link = hypOnboardingLink;
      if (!link) {
        // Create the pipeline row so the personal signup link is generated.
        // Idempotent + safe to call anytime.
        await BILLING.hyp_onboarding_start({ businessId });
        const res = await BILLING.get_hyp_status();
        const d = res?.data || {};
        setHypLive(!!d.live);
        setHypMasof(d.hypMasof || "");
        link = d.maxOnboardingLink || "";
        setHypOnboardingLink(link);
      }
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        toast.info("צרו קשר עם התמיכה כדי להפעיל סליקה");
      }
    } catch (e) {
      toast.error("שגיאה בחיבור לסליקה");
    } finally {
      setHypBusy(false);
    }
  };

  // ── Disconnect from Hyp (confirmed via modal) ──
  const handleHypDisconnect = async () => {
    setHypBusy(true);
    try {
      const res = await BILLING.hyp_disconnect({ businessId });
      if (res?.data?.code === 200 || res?.status === 200) {
        toast.success("הסליקה נותקה");
        setDisconnectOpen(false);
        fetchHypStatus();
      } else {
        toast.error(res?.data?.message || "שגיאה בניתוק הסליקה");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "שגיאה בניתוק הסליקה");
    } finally {
      setHypBusy(false);
    }
  };

  const handleChange = (e) => {
    setFormdata((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // ── Build the invoice-save payload. Mirrors the mobile screen: send the
  // invoice fields, and round-trip any legacy PaymentCred fields (zcredit
  // terminal/password, Grow) so an edit never wipes them. paymentProvider is
  // round-tripped ONLY when it is "zcredit" — the backend validation rejects
  // any other value, and the "hyp" provider is owned by the connect/disconnect
  // endpoints, not this form. ──
  const buildBody = () => {
    const { invoiceProvider, greenInvoiceApiKey, greenInvoiceSecret } = formdata;
    const external = EXTERNAL_PROVIDERS.includes(invoiceProvider);
    return {
      businessId,
      invoiceProvider,
      ...(external && !invoiceAcknowledgedAt && {
        invoiceAcknowledgedAt: new Date().toISOString(),
        invoiceAcknowledgedBy: "admin",
      }),
      ...(invoiceProvider === "greeninvoice" && {
        greenInvoiceApiKey,
        greenInvoiceSecret,
      }),
      // preserve legacy payment fields (round-trip, only when present)
      ...(existingCreds?.terminalNumber && { terminalNumber: existingCreds.terminalNumber }),
      ...(existingCreds?.password && { password: existingCreds.password }),
      ...(existingCreds?.businessType && { businessType: existingCreds.businessType }),
      // preserve Grow fields (UI removed; values must survive)
      ...(existingCreds?.growUserId && { growUserId: existingCreds.growUserId }),
      ...(existingCreds?.growPageCode && { growPageCode: existingCreds.growPageCode }),
      // preserve zcredit provider selector only (hyp rejected by validation)
      ...(existingCreds?.paymentProvider === "zcredit" && { paymentProvider: "zcredit" }),
    };
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const body = buildBody();
      if (existingCreds && existingCreds._id) {
        await BILLING.edit_payment_cred(body);
      } else {
        // add path doesn't take businessId (server derives it from the token)
        const addBody = { ...body };
        delete addBody.businessId;
        await BILLING.add_payment_cred(addBody);
      }
      toast.success("הגדרות החשבוניות נשמרו בהצלחה!");
      setAcknowledged(false);
      // Refresh so account cards + acknowledgment date reflect the new state.
      try {
        const res = await BILLING.get_payment_cred();
        if (res && res.data) setExistingCreds(res.data);
      } catch (_) {}
      if (formdata.invoiceProvider === "greeninvoice") fetchMorningAccount();
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה בשמירת ההגדרות");
    }
    setSaving(false);
  };

  const handleSubmit = () => {
    const { invoiceProvider, greenInvoiceApiKey, greenInvoiceSecret } = formdata;

    if (!EXTERNAL_PROVIDERS.includes(invoiceProvider)) {
      toast.error("יש לבחור ספק חשבוניות");
      return;
    }
    if (invoiceProvider === "greeninvoice") {
      if (!greenInvoiceApiKey.trim() || !greenInvoiceSecret.trim()) {
        toast.error("יש למלא API Key וגם API Secret");
        return;
      }
    }

    // Require numbering acknowledgment before the FIRST save of an external
    // provider (mirrors the mobile confirm dialog + stored acknowledgment date).
    if (isExternal && !invoiceAcknowledgedAt && !acknowledged) {
      setAckOpen(true);
      return;
    }
    doSave();
  };

  const confirmAcknowledge = () => {
    setAcknowledged(true);
    setAckOpen(false);
    doSave();
  };

  if (capLoading || loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <Loader />
          </div>
        </div>
      </Layout>
    );
  }

  // NOTE: non-Pro businesses are deliberately NOT blocked here any more.
  // Connecting to Hyp is an onboarding step with its own approval wait, and
  // forcing the plan purchase first meant a business had to pay before it could
  // even find out whether it would be approved for acquiring. They can connect
  // now; the banner below states plainly that USING it needs Pro, and the plan
  // can be bought in place without leaving this screen.


  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <h1 className={styles.pageTitle}>הגדרות סליקה ותשלומים</h1>

            {/* CARD 1 - ACQUIRING (HYP) */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitleGroup}>
                  <div className={styles.cardIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="5" width="20" height="14" rx="3" stroke="#DE1468" strokeWidth="1.8" />
                      <path d="M2 10h20" stroke="#DE1468" strokeWidth="1.8" />
                      <rect x="5" y="14" width="4" height="2" rx="1" fill="#DE1468" />
                    </svg>
                  </div>
                  <span className={styles.cardTitle}>סליקת אשראי</span>
                </div>
                {!hypLoading && (
                  <span className={`${styles.statusBadge} ${hypLive ? styles.connected : styles.notConfigured}`}>
                    {hypLive ? "מחובר" : "לא מחובר"}
                  </span>
                )}
              </div>

              {/* Pro requirement, stated where the decision is actually made.
                  Connecting is allowed first on purpose — Hyp has its own
                  approval step, and nobody should have to buy a plan before
                  finding out whether they can be approved at all. */}
              {!capLoading && !isPro && (
                <div className={styles.proNotice}>
                  <div className={styles.proNoticeTitle}>
                    סליקת אשראי זמינה בחבילת PRO
                  </div>
                  <p className={styles.proNoticeText}>
                    {hypLive
                      ? "החיבור ל-Hyp הושלם. כדי להתחיל לגבות תשלומים בפועל צריך לעבור לחבילת PRO."
                      : "אפשר להתחבר ל-Hyp כבר עכשיו — אישור החשבון מול Hyp לוקח זמן. כדי לגבות תשלומים בפועל תצטרכו את חבילת PRO, ואפשר לרכוש אותה כאן בסיום."}
                  </p>
                  <button
                    type="button"
                    className={styles.proNoticeBtn}
                    onClick={() => setPlanModalOpen(true)}
                  >
                    רכישת חבילת PRO
                  </button>
                </div>
              )}

              {hypLoading ? (
                <div className={styles.hypLoader}>
                  <Spinner />
                </div>
              ) : hypLive ? (
                <div className={styles.hypConnected}>
                  <p className={styles.hypConnectedTitle}>סליקה פעילה דרך Hyp ✅</p>
                  <div className={styles.hypMasofRow}>
                    <span className={styles.hypMasofLabel}>מספר מסוף</span>
                    <span className={styles.hypMasofValue}>{hypMasof || "—"}</span>
                  </div>
                  {!capLoading && !isPro && (
                    <button
                      type="button"
                      className={styles.proNoticeBtn}
                      onClick={() => setPlanModalOpen(true)}
                    >
                      להשלמת ההפעלה — רכישת חבילת PRO
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.hypDisconnectBtn}
                    onClick={() => setDisconnectOpen(true)}
                    disabled={hypBusy}
                  >
                    ניתוק סליקה
                  </button>
                </div>
              ) : (
                <div className={styles.hypConnect}>
                  <p className={styles.hypConnectTitle}>חיבור סליקת אשראי דרך Hyp</p>
                  <div className={styles.infoBox}>
                    חברו את העסק לסליקת אשראי מאובטחת דרך Hyp כדי לגבות תשלומים מלקוחות ישירות מהמערכת.
                    החיבור מתבצע דרך טופס מאובטח של Hyp.
                  </div>
                  <button
                    type="button"
                    className={styles.hypConnectBtn}
                    onClick={handleHypConnect}
                    disabled={hypBusy}
                  >
                    {hypBusy ? <Spinner /> : "התחברות לסליקה"}
                  </button>
                </div>
              )}
            </div>

            {/* CARD 2 - INVOICES */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitleGroup}>
                  <div className={styles.cardIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#DE1468" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#DE1468" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className={styles.cardTitle}>חשבוניות</span>
                </div>
              </div>

              <div className={styles.fieldsGrid}>
                <div className={styles.fieldGroupFull}>
                  <label htmlFor="invoiceProvider">ספק חשבוניות</label>
                  <select
                    id="invoiceProvider"
                    name="invoiceProvider"
                    value={formdata.invoiceProvider}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      בחר ספק חשבוניות
                    </option>
                    <option value="greeninvoice">Morning</option>
                  </select>
                </div>
              </div>

              {isLegacyZcredit && !isExternal && (
                <div className={styles.legacyNote}>
                  בעבר הופקו חשבוניות דרך ZCredit. יש לבחור ספק חשבוניות (Morning) ולשמור.
                </div>
              )}

              {/* Morning fields */}
              {formdata.invoiceProvider === "greeninvoice" && (
                <>
                  {morningAccount?.name && (
                    <div className={styles.accountCard}>
                      מחובר: {morningAccount.name}
                      {morningAccount.taxId ? ` · ח.פ/ע.מ ${morningAccount.taxId}` : ""}
                    </div>
                  )}
                  <div className={styles.infoBox}>
                    לאחר כל תשלום מוצלח, חשבונית תישלח אוטומטית ללקוח במייל.
                    לקבלת פרטי API: morning.co.il ← הגדרות ← API
                  </div>
                  <div className={styles.fieldsGrid}>
                    <div className={styles.fieldGroup}>
                      <label htmlFor="greenInvoiceApiKey">מזהה (API Key) *</label>
                      <input
                        id="greenInvoiceApiKey"
                        name="greenInvoiceApiKey"
                        type="text"
                        placeholder="הדבק את ה-API Key מ-Morning"
                        value={formdata.greenInvoiceApiKey}
                        onChange={handleChange}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <label htmlFor="greenInvoiceSecret">סיסמה (API Secret) *</label>
                      <input
                        id="greenInvoiceSecret"
                        name="greenInvoiceSecret"
                        type="password"
                        placeholder="הדבק את ה-API Secret מ-Morning"
                        value={formdata.greenInvoiceSecret}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </>
              )}

              {isExternal && invoiceAcknowledgedAt && (
                <div className={styles.ackDone}>
                  ✓ מספור חשבוניות אושר בתאריך{" "}
                  {new Date(invoiceAcknowledgedAt).toLocaleDateString("he-IL")}
                </div>
              )}
              {isExternal && !invoiceAcknowledgedAt && (
                <div className={styles.ackWarn}>
                  לפני שמירה, יש לוודא שהמספור במערכת {PROVIDER_LABEL[formdata.invoiceProvider]} תקין
                  ורציף. בעת השמירה תתבקשו לאשר.
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          className={styles.saveBtn}
          disabled={saving}
        >
          {saving ? "שומר..." : "שמירת שינויים"}
        </button>
      </div>

      {/* DISCONNECT CONFIRM */}
      {/* One-click plan purchase, in place. These are the SAME two components the
          subscription screen uses — card details go to the one already-audited
          endpoint rather than a second payment path invented for this screen. */}
      {planModalOpen && (
        <SelectPlan
          setIsOpen={setPlanModalOpen}
          setIsPaymentModalOpen={setPayModalOpen}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
        />
      )}
      {payModalOpen && (
        <Payment setIsOpen={setPayModalOpen} selectedPlan={selectedPlan} />
      )}

      {disconnectOpen && (
        <Modal setIsOpen={setDisconnectOpen} onClick={() => null}>
          <div className={styles.confirm}>
            <h2>ניתוק סליקה</h2>
            <p>לנתק את הסליקה? לא תוכלו לגבות בכרטיס אשראי עד לחיבור מחדש.</p>
            <div className={styles.confirmBtns}>
              <button
                className={styles.confirmDanger}
                onClick={handleHypDisconnect}
                disabled={hypBusy}
              >
                {hypBusy ? <Spinner /> : "ניתוק סליקה"}
              </button>
              <button className={styles.confirmCancel} onClick={() => setDisconnectOpen(false)}>
                ביטול
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* NUMBERING ACKNOWLEDGMENT CONFIRM */}
      {ackOpen && (
        <Modal setIsOpen={setAckOpen} onClick={() => null}>
          <div className={styles.confirm}>
            <h2>אישור מספור חשבוניות</h2>
            <p>
              לפני חיבור ספק חשבוניות, יש לוודא שהמספור במערכת החיצונית
              ({PROVIDER_LABEL[formdata.invoiceProvider]}) תקין ורציף. אנא אשרו שבדקתם את המספור.
            </p>
            <div className={styles.confirmBtns}>
              <button className={styles.confirmPrimary} onClick={confirmAcknowledge}>
                אני מאשר/ת
              </button>
              <button className={styles.confirmCancel} onClick={() => setAckOpen(false)}>
                ביטול
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default PaymentSettings;
